import {useEffect, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {filterAudienceTargeting, getRenderedAbArms} from '../utils/gdi-audience-match'
import {flushFxImpressions, queueFxImpression} from '../utils/optimizely-fx-events'
import {useOptimizelyFxConfig} from './use-optimizely-fx-config'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'
import {useAudienceState} from '../contexts/audience-context'
import {usePreviewContext} from '../contexts/preview-context'
/* Detection only — the draft-merge machinery and the link backfill are pulled in
 * dynamically below, so a shopper never downloads them. */
import {getPreviewProjectId, syncPreviewSession} from '../utils/optimizely-preview-session'

/* One-shot debug dump of the resolved page payload. Replaces the per-component
 * logs in contentRenderer.jsx so the console gets a single collapsed entry per
 * page fetch instead of dozens of per-render entries. */
const DEBUG_LOG_OPTIMIZELY = false

/**
 * Deep merge preview content into fetched data
 * Finds blocks by ContentLink.GuidValue and replaces with preview version
 * When the preview IS the page itself (ContainerPage), replace the entire page
 */
const mergePreviewContent = (data, previewContent) => {
    if (!data || !previewContent?.item) {
        return data
    }

    const previewItem = previewContent.item
    const previewKey = previewItem?.ContentLink?.GuidValue

    if (!previewKey) {
        return data
    }

    // Check if the preview content IS the page itself (ContainerPage)
    // — both data.item and preview.item have the same GuidValue.
    if (data?.item?.ContentLink?.GuidValue === previewKey) {
        // Replace the entire page with preview content, preserving the `item` shape.
        return {
            ...data,
            item: previewItem
        }
    }

    // Helper to recursively find and replace content by GuidValue
    const replaceContent = (obj) => {
        if (!obj || typeof obj !== 'object') return obj

        // Check if this object has a matching ContentLink.GuidValue
        if (obj?.ContentLink?.GuidValue === previewKey) {
            // Replace the Expanded content with preview content
            return {
                ...obj,
                ContentLink: {
                    ...obj.ContentLink,
                    Expanded: previewItem
                }
            }
        }

        // Recursively process arrays
        if (Array.isArray(obj)) {
            return obj.map((item) => replaceContent(item))
        }

        // Recursively process object properties
        const result = {}
        for (const key of Object.keys(obj)) {
            result[key] = replaceContent(obj[key])
        }
        return result
    }

    return replaceContent(data)
}

export function useOptimizelyPage(customPath) {
    const location = useLocation()
    // Use custom path if provided (e.g., 'header', 'homepage', 'pdp')
    // If customPath is explicitly null, don't fall back to pathname (query will be disabled)
    // If customPath is undefined, fall back to current route pathname
    const address = customPath === null ? null : (customPath || location.pathname)
    const {site} = useMultiSite()

    // Brand + locale select the Optimizely content tree (`/{locale}/{brandSlug}/`).
    const {brandSlug, localeId} = useOptimizelyFxConfig()

    /* No shopper identity touches this request: the response is the same for
     * every shopper (one CDN copy per page) and audience targeting is resolved
     * client-side on the tree below, from `useAudienceState`. */
    const audience = useAudienceState()

    const isSSR = typeof window === 'undefined'
    const fetchOrigin = useSsrFetchOrigin()

    // Check if we're in Optimizely preview mode and this matches the preview page
    // identifier. pageIdentifier may be an array (e.g. a GDICartPage feeds both
    // the 'cart' and 'cart-minicart' reads); the actual replace stays GuidValue-
    // gated in mergePreviewContent, so listing extra identifiers is safe.
    const {previewContent, pageIdentifier, isPreviewMode} = usePreviewContext()
    const shouldMergePreview =
        isPreviewMode &&
        (Array.isArray(pageIdentifier)
            ? pageIdentifier.includes(customPath)
            : pageIdentifier === customPath)

    // MRT preview: get project ID from sessionStorage or URL param
    const previewProjectId =
        typeof window !== 'undefined'
            ? getPreviewProjectId() ||
              new URLSearchParams(location.search).get('optimizely_project')
            : null
    const isMrtPreview = !!previewProjectId

    // Preview session (cookie): the single signal for cache-bypass + drafts.
    // ?preview=<password> enters/refreshes it, ?preview=false exits. Once set it
    // persists across navigation/reload, so preview survives even when the URL
    // is just ?optimizely_project=… . The value (password in prod) is threaded
    // onto fetches below since PWA Kit strips the Cookie header server-side.
    const previewValue = typeof window !== 'undefined' ? syncPreviewSession() : null
    const cacheBypassActive = Boolean(previewValue)

    const query = useQuery({
        /* brand + locale partition the cache so the 6 (brand × locale) trees
         * never collide. previewProjectId stays so preview swaps refetch.
         * cacheBypassActive forces React Query to refetch when CMS team toggles. */
        queryKey: [
            'optimizely-page',
            address,
            brandSlug,
            localeId,
            previewProjectId,
            cacheBypassActive
        ],
        queryFn: async () => {
            const params = new URLSearchParams({
                path: address,
                brand: brandSlug,
                locale: localeId
            })
            if (previewProjectId) {
                params.set('optimizely_project', previewProjectId)
            }
            if (cacheBypassActive) {
                // Forward the real ?preview value (the password in prod) so the
                // server can authorize the cache bypass.
                params.set('preview', previewValue)
            }

            // Deliberately header-free: any identity here would fragment or
            // bypass the shared CDN cache.
            const res = await fetch(`${fetchOrigin}/optimizely-page?${params}`)
            const json = await res.json()

            // Handle both old single query format and new combined query format
            let pageData
            if (json?.data?.page) {
                // New combined query format
                pageData = json.data.page
            } else if (json?.data?.Content) {
                // Old single query format
                pageData = json.data
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Unknown Optimizely data format')
                }
                pageData = json?.data
            }

            // Apply MRT preview blocks client-side (supplements server-side application)
            if (isMrtPreview && pageData) {
                // Re-fetch fresh blocks from CMS API and update sessionStorage.
                // Pass languageCode (active locale) + siteId so the proxy fetches
                // the right language tree — not the hardcoded en-GB default.
                try {
                    const blocksParams = new URLSearchParams()
                    blocksParams.set('languageCode', localeId || 'en-GB')
                    if (site?.id) blocksParams.set('siteId', site.id)
                    // Forward the preview password so the proxy authorizes in prod.
                    if (previewValue) blocksParams.set('preview', previewValue)
                    const blocksRes = await fetch(
                        `/api/cms-proxy/projects/${previewProjectId}/blocks?${blocksParams}`
                    )
                    if (blocksRes.ok) {
                        const freshData = await blocksRes.json()
                        if (typeof window !== 'undefined') {
                            // Full draft-projects API response (blocks + pages).
                            console.log(`[Preview] projects API response for ${address}:`, freshData)
                        }
                        const {storePreviewData} = await import('../utils/optimizely-preview')
                        storePreviewData(freshData, previewProjectId)
                    }
                } catch (e) {
                    console.warn('[Preview] Could not refresh blocks from CMS:', e)
                }

                /* The server resolved category/product URLs on the PUBLISHED
                 * payload only. Remember those, merge, then backfill the
                 * draft-built blocks — otherwise their links render as
                 * /category/<id>. Only references never seen resolved before
                 * cost a request. */
                const [{applyPreviewBlocks}, linkBackfill] = await Promise.all([
                    import('../utils/optimizely-preview'),
                    import('../utils/optimizely-link-backfill')
                ])
                const {harvestResolvedLinks, backfillKnownLinks, applyResolvedLinks} = linkBackfill

                harvestResolvedLinks(pageData)
                pageData = applyPreviewBlocks(pageData)

                const missing = backfillKnownLinks(pageData)
                if (missing.categoryIds.length || missing.productIds.length) {
                    try {
                        const linkParams = new URLSearchParams()
                        if (missing.categoryIds.length)
                            linkParams.set('categoryIds', missing.categoryIds.join(','))
                        if (missing.productIds.length)
                            linkParams.set('productIds', missing.productIds.join(','))
                        if (previewValue) linkParams.set('preview', previewValue)
                        const linkRes = await fetch(`/api/preview-resolve-links?${linkParams}`)
                        if (linkRes.ok) applyResolvedLinks(pageData, await linkRes.json())
                    } catch (e) {
                        console.warn('[Preview] Could not resolve draft link URLs:', e)
                    }
                }
            }

            return pageData
        },
        // Bypass client cache when MRT preview is active
        staleTime: isMrtPreview ? 0 : 24 * 60 * 60 * 1000,
        // Keep in cache for 24 hours
        gcTime: 24 * 60 * 60 * 1000,
        // Only fetch when we have a valid address (prevents fetching with null customPath)
        enabled: !!address,
        // Refetch on mount/focus when in preview mode to pick up CMS changes
        refetchOnWindowFocus: isMrtPreview,
        refetchOnMount: isMrtPreview,
        refetchOnReconnect: false
    })

    /* Single collapsed console dump per resolved page payload. Driven by
     * `query.data` so it fires on hydrated cache hits too — logging inside
     * `queryFn` would be skipped when React Query rehydrates from a previous
     * fetch (24h `staleTime`) or from SSR. */
    useEffect(() => {
        if (!DEBUG_LOG_OPTIMIZELY || isSSR || !query.data) return
        const typename =
            query.data?.item?.__typename || query.data?.__typename || 'unknown'
        console.groupCollapsed(`📄 Optimizely page — ${address} (${typename})`)
        console.log(query.data)
        console.groupEnd()
    }, [query.data, address, isSSR])

    /* Preview merge first (draft blocks carry targeting too), then the audience
     * filter. `renderAll` until mount keeps the hydration render identical to
     * the SSR HTML; the pre-paint stylesheet has already hidden the wrong
     * variants, so the post-mount prune is invisible. */
    const merged = useMemo(
        () =>
            shouldMergePreview && query.data
                ? mergePreviewContent(query.data, previewContent)
                : query.data,
        [shouldMergePreview, query.data, previewContent]
    )
    const data = useMemo(() => filterAudienceTargeting(merged, audience), [merged, audience])

    // Impressions for the A/B arms this render kept — once per flag per page view.
    const renderedAb = getRenderedAbArms(data)
    const pageKey = location.key || location.pathname
    useEffect(() => {
        if (!renderedAb.length) return
        renderedAb.forEach(([flagKey, variationKey]) =>
            queueFxImpression(flagKey, variationKey, pageKey)
        )
        flushFxImpressions()
    }, [renderedAb, pageKey])

    return data === query.data ? query : {...query, data}
}
